import { post, get } from '../../request/http';
import { AsyncQueryTimeout, AsyncQueryCancelled } from './snowflake/records';
import { ASYNC_REPORT_RESOLVE_REPORT_TYPE, ASYNC_REPORT_RESOLVE_REPORT_REQUEST_CACHE_ID_API } from '../../apis/asyncReportResolveApi';
import { REPORT_REQUEST_CACHE_STATUS, DEFAULT_POLL_INTERVAL, DEFAULT_MAX_POLL_ATTEMPTS, RELATIONAL_REPORT_POLL_INTERVAL, RELATIONAL_REPORT_MAX_POLL_ATTEMPTS } from '../../constants/asyncReportResolve';
const DELAY_BEFORE_POLLING = 200;
/**
 * behavior:
 * It will keep doing the polling until it gets the result or the max attempts is reached
 * If it throws exception it will stop the polling
 */
export const fetchReportWithPolling = async ({
  payload,
  asyncQueryCachedId,
  refresh,
  pollEndpoint,
  pollInterval = DEFAULT_POLL_INTERVAL,
  maxPollAttempts = DEFAULT_MAX_POLL_ATTEMPTS,
  delayBeforePolling = DELAY_BEFORE_POLLING,
  signal
}) => {
  // Step 1: Get the cache ID
  let cachedId = asyncQueryCachedId;
  if (!cachedId) {
    // TODO: pass signal to the http client, currently it is not supported yet
    // currently it is a lower priority given how fast the SLA for this asyc endpoint.
    const getIdResponse = await post(pollEndpoint, {
      data: payload
    }, {
      // If refresh is true, we do not want to get data from the redux cache
      bust: refresh
    });
    cachedId = getIdResponse.get('id');
  }

  // Add a delay before doing the polling
  // without the delay, the chance that we are getting the response from the first poll is very low
  await new Promise(resolve => setTimeout(resolve, delayBeforePolling));

  // Step 2: Poll for results
  let attempts = 0;
  const poll = async () => {
    if (signal !== null && signal !== void 0 && signal.aborted) {
      throw new AsyncQueryCancelled(`Async query ${cachedId} was cancelled`);
    }
    if (attempts >= maxPollAttempts) {
      throw new AsyncQueryTimeout(`Async query ${cachedId} timed out`);
    }
    const response = await get(`${pollEndpoint}/${cachedId}`, {}, {
      // First attempt - always ready from the redux cache if it is available
      // Next attempts - always bust the cache, but still store the result in the redux cache
      bust: attempts !== 0
    });
    const taskStatus = response.get('taskStatus');
    if (taskStatus === REPORT_REQUEST_CACHE_STATUS.COMPLETED) {
      const responseResult = response.get('result');
      return responseResult;
    }
    attempts++;
    await new Promise(resolve => setTimeout(resolve, pollInterval));
    return poll();
  };
  return poll();
};

/**
 * Options for fetching async reports
 * @interface FetchAsyncReportOptions
 * @property {string} [asyncQueryCachedId] - Optional cached ID for the async query
 * @property {boolean} [refresh] - Whether to refresh the data instead of using cache
 * @property {AbortSignal} [signal] - Optional AbortSignal to cancel the request
 */

/**
 * Generic props interface for fetching async reports
 * @interface FetchAsyncReportProps
 * @template TPayload - The type of the payload to be sent
 * @property {TPayload} payload - The data payload for the report
 * @property {FetchAsyncReportOptions} [options] - Optional configuration for the request
 */

/**
 * Props for fetching async relational reports
 * @typedef {FetchAsyncReportProps<ReportWithOptions>} FetchAsyncRelationalReportProps
 */

/**
 * Props for fetching async config reports
 * @typedef {FetchAsyncReportProps<ConfigReport>} FetchAsyncConfigReportProps
 */

/**
 * Props for fetching async multi-config reports
 * @typedef {FetchAsyncReportProps<MultiConfigReport>} FetchAsyncMultiConfigReportProps
 */

export const fetchAsyncRelationalReport = async ({
  payload,
  options
}) => {
  const {
    asyncQueryCachedId,
    signal
  } = options || {};
  const pollEndpoint = ASYNC_REPORT_RESOLVE_REPORT_REQUEST_CACHE_ID_API[ASYNC_REPORT_RESOLVE_REPORT_TYPE.RELATIONAL];
  const response = await fetchReportWithPolling({
    payload,
    pollEndpoint,
    asyncQueryCachedId,
    pollInterval: RELATIONAL_REPORT_POLL_INTERVAL,
    maxPollAttempts: RELATIONAL_REPORT_MAX_POLL_ATTEMPTS,
    signal
  });
  return response;
};
export const fetchAsyncConfigReport = async ({
  payload,
  options
}) => {
  const {
    asyncQueryCachedId,
    signal
  } = options || {};
  const pollEndpoint = ASYNC_REPORT_RESOLVE_REPORT_REQUEST_CACHE_ID_API[ASYNC_REPORT_RESOLVE_REPORT_TYPE.CONFIG];
  const response = await fetchReportWithPolling({
    payload,
    pollEndpoint,
    asyncQueryCachedId,
    signal
  });
  return response;
};
export const fetchAsyncMultiConfigReport = async ({
  payload,
  options
}) => {
  const {
    asyncQueryCachedId,
    signal
  } = options || {};
  const pollEndpoint = ASYNC_REPORT_RESOLVE_REPORT_REQUEST_CACHE_ID_API[ASYNC_REPORT_RESOLVE_REPORT_TYPE.MULTI_CONFIG];
  const response = await fetchReportWithPolling({
    payload,
    pollEndpoint,
    asyncQueryCachedId,
    signal
  });
  return response;
};